import React, { useRef } from 'react';

import { AudioCaptionsContextProvider } from '../../../components/Audio/AudioCaptionsContextProvider';
import { useIsMobile } from '../../../hooks/useIsMobile';
import { Guid } from '../../../store/model/guid';
import { AssertionException } from '../../../utils/log';
import { MemoizedContentRequirementsProvider } from '../../RestrictNavigation/ContentRequirementsProvider';
import { DesktopNonSequencedFlow } from './DesktopNonSequencedFlow';
import { MobileNonSequencedFlow } from './mobile/MobileNonSequencedFlow';
import { HotspotTab, ModalSize } from '@ael/core-api';
import * as Immutable from 'immutable';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { TransitionStatus } from 'react-transition-group/Transition';

import './HotspotContent.scss';
import { FroalaEditorsSettings } from 'src/config/configs';

interface HotspotContentProps {
  state: TransitionStatus;
  tabs?: Immutable.List<HotspotTab>;
  hotspotId?: Guid;
  onDismiss: () => void;
  onTabViewed: (tabId: string) => void;
  modalSize: ModalSize;
  froalaEditorsSettings: FroalaEditorsSettings;
}

export const HotspotContent: React.FC<HotspotContentProps> = ({
  tabs,
  onDismiss,
  hotspotId,
  onTabViewed,
  state,
  modalSize,
  froalaEditorsSettings,
}) => {
  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);
  const ref = useRef(null);
  const isMobile = useIsMobile();

  if (!tabs || !hotspotId) {
    return null;
  }

  const onTabSelected = (tabIndex: number) => {
    if (tabIndex >= 0 && tabIndex < tabs.size) {
      setSelectedTabIndex(tabIndex);
      if (tabs) {
        const tab = tabs.get(tabIndex);
        if (!tab) {
          throw AssertionException('Tab cannot be undefined');
        }
        onTabViewed(tab.id);
      }
    }
  };

  const selectedTab = tabs.get(selectedTabIndex);
  if (!selectedTab) {
    throw AssertionException('Selected tab cannot be undefined');
  }

  const goToNext = () => {
    const newSelectedTabIndex = selectedTabIndex + 1;
    if (newSelectedTabIndex < tabs.size) {
      onTabSelected(newSelectedTabIndex);
    }
  };

  const goToPrevious = () => {
    const newSelectedTabIndex = selectedTabIndex - 1;
    if (newSelectedTabIndex >= 0) {
      onTabSelected(newSelectedTabIndex);
    }
  };

  return (
    <TransitionGroup>
      <CSSTransition nodeRef={ref} timeout={450} classNames="fade" in={state !== 'exiting'} mountOnEnter appear>
        <AudioCaptionsContextProvider>
          <MemoizedContentRequirementsProvider item={selectedTab}>
            {!isMobile ? (
              <DesktopNonSequencedFlow
                ref={ref}
                tabs={tabs}
                selectedTab={selectedTab}
                goToNext={goToNext}
                goToPrevious={goToPrevious}
                selectedTabIndex={selectedTabIndex}
                onTabSelected={onTabSelected}
                onDismiss={onDismiss}
                modalSize={modalSize}
                froalaEditorsSettings={froalaEditorsSettings}
              />
            ) : (
              <MobileNonSequencedFlow
                ref={ref}
                tabs={tabs}
                selectedTab={selectedTab}
                goToNext={goToNext}
                goToPrevious={goToPrevious}
                selectedTabIndex={selectedTabIndex}
                onTabSelected={onTabSelected}
                onDismiss={onDismiss}
                froalaEditorsSettings={froalaEditorsSettings}
              />
            )}
          </MemoizedContentRequirementsProvider>
        </AudioCaptionsContextProvider>
      </CSSTransition>
    </TransitionGroup>
  );
};
